import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  InvoicesControllerListInvoicesParams,
  ReissueInvoiceDto,
  SendReminderEmailDto,
} from '@trellis/api'
import type {
  BatchUpdateInvoiceLineItems,
  CreateInvoice,
  DuplicateInvoice,
  InvoiceStatus,
  RecordPayment,
  UpdateInvoice,
  UpdateInvoiceLineItem,
} from '@trellis/shared'
import { z } from 'zod'

import { usePagedInfiniteQuery } from '@/hooks/usePagedInfiniteQuery'
import {
  INVOICE_REMINDERS_QUERY_KEY,
  REMINDER_RATE_LIMITED_ERROR_CODE,
} from '@/hooks/useReminders'
import { pushEvent } from '@/lib/analytics'
import {
  archiveInvoice,
  assignTagsToInvoice,
  batchUpdateInvoiceLineItems,
  checkInvoiceNumberAvailability,
  createInvoice,
  deleteInvoice,
  deleteInvoiceLineItem,
  deletePayment,
  duplicateInvoice,
  getInvoice,
  getInvoicePreview,
  getNextInvoiceNumber,
  getPaymentReceiptUrl,
  linkReissuedInvoice,
  listInvoices,
  recordPayment,
  reissueInvoice,
  removeTagFromInvoice,
  sendReminderEmail,
  syncAllInvoiceLineItems,
  syncInvoiceLineItem,
  transitionInvoiceStatus,
  unarchiveInvoice,
  unlinkReissuedInvoice,
  updateInvoice,
  updateInvoiceLineItem,
  uploadPaymentReceipt,
} from '@/requests/invoices'

/** Fetches all invoices (up to 500) for KPI strip and tab count computation. */
export function useInvoicesSummaryQuery() {
  return useQuery({
    queryKey: ['invoices', 'summary'],
    queryFn: () => listInvoices({ limit: 100 }),
    staleTime: 60_000,
  })
}

/**
 * Shared with the invoices list route's loader ({@link invoicesInfiniteQueryOptions}) so
 * a prefetched loader cache entry and this hook's read land on the same query key.
 */
export function invoicesInfiniteQueryOptions(
  options: InvoicesControllerListInvoicesParams = {},
) {
  return infiniteQueryOptions({
    queryKey: ['invoices', options],
    queryFn: ({ pageParam }) =>
      listInvoices({ ...options, cursor: pageParam ?? options.cursor }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.nextCursor ?? undefined,
  })
}

export function useInvoicesQuery(
  options: InvoicesControllerListInvoicesParams = {},
  queryOptions: { enabled?: boolean } = {},
) {
  const limit = options.limit ?? 20

  const query = useInfiniteQuery({
    ...invoicesInfiniteQueryOptions(options),
    enabled: queryOptions.enabled,
  })

  return { ...query, ...usePagedInfiniteQuery(query, limit) }
}

export function useInvoiceQuery(id: string) {
  return useQuery({
    queryKey: ['invoices', id],
    queryFn: () => getInvoice(id),
    enabled: !!id,
  })
}

export function useInvoicePreviewQuery(id: string | null) {
  return useQuery({
    queryKey: ['invoices', id, 'preview'],
    queryFn: () => getInvoicePreview(id!),
    enabled: !!id,
  })
}

export function useCreateInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateInvoice) => {
      const result = await createInvoice(data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      pushEvent('invoice_created')
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: () => pushEvent('form_submit_failed', { form_id: 'invoice' }),
  })
}

export function useUpdateInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateInvoice }) => {
      const result = await updateInvoice(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['invoices', variables.id] })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export function useDeleteInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteInvoice(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export function useDuplicateInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data?: DuplicateInvoice }) => {
      const result = await duplicateInvoice(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export function useReissueInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data?: ReissueInvoiceDto }) => {
      const result = await reissueInvoice(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
    },
  })
}

export function useLinkReissuedInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, invoiceId }: { id: string; invoiceId: string }) => {
      const result = await linkReissuedInvoice(id, invoiceId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useUnlinkReissuedInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await unlinkReissuedInvoice(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

/** Non-consuming preview of the next system-generated invoice number. */
export function useNextInvoiceNumberQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['invoices', 'next-number'],
    queryFn: getNextInvoiceNumber,
    enabled,
    staleTime: 0,
  })
}

/** Real-time uniqueness check backing the invoice reference field. */
export function useInvoiceNumberAvailabilityQuery(
  invoiceNumber: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['invoices', 'number-availability', invoiceNumber],
    queryFn: () => checkInvoiceNumberAvailability(invoiceNumber),
    enabled: enabled && invoiceNumber.trim().length > 0,
    staleTime: 0,
  })
}

export function useTransitionInvoiceStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: InvoiceStatus }) => {
      const result = await transitionInvoiceStatus(id, status)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      if (variables.status === 'SENT') pushEvent('invoice_sent')
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['invoices', variables.id] })
      // Sending an invoice flips billed=true on attached expenses — refresh them
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      // Sending an invoice may auto-activate a PROSPECT client.
      await queryClient.invalidateQueries({ queryKey: ['clients'] })
      // Sending enrols reminders; paying, voiding and cancelling cancel them.
      await queryClient.invalidateQueries({ queryKey: INVOICE_REMINDERS_QUERY_KEY })
    },
  })
}

export function useArchiveInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await archiveInvoice(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['invoices', id] })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      await queryClient.invalidateQueries({ queryKey: INVOICE_REMINDERS_QUERY_KEY })
    },
  })
}

export function useUnarchiveInvoiceMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await unarchiveInvoice(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['invoices', id] })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

/** Send now was refused because a reminder email went out in the last 24 hours. */
export class ReminderRateLimitedError extends Error {
  constructor(
    message: string,
    readonly nextAllowedAt: string | null,
  ) {
    super(message)
    this.name = 'ReminderRateLimitedError'
  }
}

const RateLimitDetailsSchema = z.object({ nextAllowedAt: z.iso.datetime() })

export function useSendReminderEmailMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SendReminderEmailDto }) => {
      const result = await sendReminderEmail(id, data)
      if (result.error) {
        if (result.error.code === REMINDER_RATE_LIMITED_ERROR_CODE) {
          const details = RateLimitDetailsSchema.safeParse(result.error.details)
          throw new ReminderRateLimitedError(
            result.error.message,
            details.success ? details.data.nextAllowedAt : null,
          )
        }
        throw new Error(result.error.message)
      }
      return result
    },
    onSuccess: async (_, variables) => {
      pushEvent('invoice_reminder_sent')
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['invoices', variables.id] })
      await queryClient.invalidateQueries({ queryKey: ['notifications', 'summary'] })
      await queryClient.invalidateQueries({ queryKey: INVOICE_REMINDERS_QUERY_KEY })
    },
  })
}

export function useBatchUpdateInvoiceLineItemsMutation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: BatchUpdateInvoiceLineItems) => {
      const result = await batchUpdateInvoiceLineItems(invoiceId, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
    },
  })
}

export function useUpdateInvoiceLineItemMutation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateInvoiceLineItem }) => {
      const result = await updateInvoiceLineItem(invoiceId, id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
    },
  })
}

export function useDeleteInvoiceLineItemMutation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteInvoiceLineItem(invoiceId, id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
    },
  })
}

export function useSyncInvoiceLineItemMutation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await syncInvoiceLineItem(invoiceId, id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
    },
  })
}

export function useSyncAllInvoiceLineItemsMutation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const result = await syncAllInvoiceLineItems(invoiceId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
    },
  })
}

export function useAssignInvoiceTagsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, tagIds }: { invoiceId: string; tagIds: string[] }) =>
      assignTagsToInvoice(invoiceId, tagIds),
    onSuccess: async (_, { invoiceId }) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
    },
  })
}

export function useRemoveInvoiceTagMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, tagId }: { invoiceId: string; tagId: string }) =>
      removeTagFromInvoice(invoiceId, tagId),
    onSuccess: async (_, { invoiceId }) => {
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
    },
  })
}

export function useRecordPaymentMutation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: RecordPayment) => {
      const result = await recordPayment(invoiceId, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      pushEvent('invoice_payment_recorded')
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
      await queryClient.invalidateQueries({ queryKey: ['stripe', 'payments', invoiceId] })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
      await queryClient.invalidateQueries({ queryKey: INVOICE_REMINDERS_QUERY_KEY })
    },
  })
}

export function useDeletePaymentMutation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (paymentId: string) => {
      const result = await deletePayment(invoiceId, paymentId)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] })
      await queryClient.invalidateQueries({ queryKey: ['stripe', 'payments', invoiceId] })
      await queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export function usePaymentReceiptQuery(
  invoiceId: string,
  paymentId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['invoices', invoiceId, 'payments', paymentId, 'receipt'],
    queryFn: () => getPaymentReceiptUrl(invoiceId, paymentId),
    enabled: enabled && !!invoiceId && !!paymentId,
    staleTime: 14 * 60 * 1000,
  })
}

export function useUploadPaymentReceiptMutation(invoiceId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ paymentId, file }: { paymentId: string; file: File }) => {
      const result = await uploadPaymentReceipt(invoiceId, paymentId, file)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['stripe', 'payments', invoiceId] })
      await queryClient.invalidateQueries({
        queryKey: ['invoices', invoiceId, 'payments', variables.paymentId, 'receipt'],
      })
    },
  })
}
